import type { DefinitionComponent, ExpandedSurvey } from '@aidkitorg/types/lib/survey';
import { encodeWaypoint } from '@aidkitorg/types/lib/translation/componentIdentifier';
import { referenceBlocksArray, referenceObjectsArray } from '@aidkitorg/types/lib/translation/references';
import type { MatchableText, MatchType } from './ConfigSearch';

export type EditorLocation = (string | number)[];
export type DefinitionMatch = MatchableText & { definitionId: string };
export type DefinitionSearchResult = {
  thing: any;
  tree: string[];
  tab: string;
  location: EditorLocation;
  matchTypes: MatchType[];
  definition?: DefinitionComponent;
  definitionLocation?: EditorLocation;
  definitionMatches: DefinitionMatch[];
  invalidReference?: boolean;
};

const referenceKinds = new Set<string>([...referenceBlocksArray, ...referenceObjectsArray]);
export function isDefinitionReference(node: any): boolean {
  return !!node && referenceKinds.has(node.kind) && typeof node.definitionId === 'string';
}

export function readDefinition(definition: DefinitionComponent) {
  return definition.kind === 'Definition'
    ? { id: definition.id, label: definition.label || '', component: definition.component }
    : { id: definition.definitionProps.id, label: definition.definitionProps.label || '', component: definition };
}

/** Find References at their locations in the current configuration. */
export function findDefinitionUses(
  survey: ExpandedSurvey,
  matchOwnText: (node: any) => MatchableText[],
  includeDashboards: boolean,
): DefinitionSearchResult[] {
  type Occurrence = { result: DefinitionSearchResult; owner?: string };
  const definitions = new Map<string, DefinitionSearchResult>();
  const uses = new Map<string, Occurrence[]>();
  const results: DefinitionSearchResult[] = [];
  const ownMatches = new Map<string, MatchableText[]>();

  function walk(node: any, location: EditorLocation, tree: string[], owner?: string) {
    if (!node || typeof node !== 'object') {
      if (owner && (typeof node === 'string' || typeof node === 'number')) {
        ownMatches.get(owner)!.push(...matchOwnText({ value: node }));
      }
      return;
    }
    if (Array.isArray(node)) {
      node.forEach((child, i) => walk(child, [...location, i], tree, owner));
      return;
    }
    if (!includeDashboards && (node.kind === 'Dashboard' || node.kind === 'Dashboard Section' || node.kind === 'Custom Query')) return;
    const nextTree = node.kind ? [...tree, encodeWaypoint(node, typeof location[location.length - 1] === 'number' ? location[location.length - 1] as number : 0)] : tree;
    const isDefinition = location[0] === 'definitions' &&
      (node.kind === 'Definition' || node.definitionProps?.kind === 'Definition');
    const isReference = isDefinitionReference(node);
    let enclosing = owner;
    if (isDefinition || isReference) {
      const result: DefinitionSearchResult = {
        thing: node, tree: nextTree, tab: String(location[0]), location,
        matchTypes: [], definitionMatches: [],
      };
      results.push(result);
      if (isDefinition) {
        const definition = readDefinition(node);
        enclosing = definition.id;
        definitions.set(definition.id, result);
        ownMatches.set(definition.id, node.kind === 'Definition' ? matchOwnText(node) : matchOwnText({ kind: 'Definition', label: definition.label }));
      } else {
        const list = uses.get(node.definitionId) || [];
        list.push({ result, owner });
        uses.set(node.definitionId, list);
        const matches = matchOwnText(node);
        result.matchTypes = Array.from(new Set(matches.map(m => m.matchType)));
        if (owner) ownMatches.get(owner)!.push(...matches);
      }
    }
    // Match this component's fields before visiting its children.
    if (enclosing && (!isDefinition || node.kind !== 'Definition') && !isReference) {
      ownMatches.get(enclosing)!.push(...matchOwnText(node));
    }
    for (const [key, value] of Object.entries(node)) {
      if (key === 'definitionProps') {
        if (enclosing && value && typeof value === 'object') ownMatches.get(enclosing)!.push(...matchOwnText(value));
        continue;
      }
      walk(value, [...location, key], nextTree, enclosing);
    }
  }
  for (const [tab, content] of Object.entries(survey)) walk(content, [tab], []);

  for (const [id, occurrences] of Array.from(uses.entries())) {
    const definition = definitions.get(id);
    for (const { result } of occurrences) {
      result.definition = definition?.thing;
      result.definitionLocation = definition?.location;
      result.invalidReference = !definition || result.thing.kind !== `${readDefinition(definition.thing).component.kind} Reference`;
    }
  }

  // Visit each Definition once to stop cycles, but return every Reference to it.
  for (const [sourceId, matches] of Array.from(ownMatches.entries())) {
    if (!matches.length) continue;
    const evidence = Array.from(new Map(matches.map(m => [JSON.stringify(m), { ...m, definitionId: sourceId }])).values());
    const queue = [sourceId];
    const visited = new Set(queue);
    const add = (result: DefinitionSearchResult) => {
      result.matchTypes = Array.from(new Set([...result.matchTypes, ...matches.map(m => m.matchType)]));
      result.definitionMatches.push(...evidence);
    };
    for (let i = 0; i < queue.length; i++) {
      const id = queue[i];
      const definition = definitions.get(id);
      if (definition) add(definition);
      for (const occurrence of uses.get(id) || []) {
        add(occurrence.result);
        if (occurrence.owner && !visited.has(occurrence.owner)) {
          visited.add(occurrence.owner);
          queue.push(occurrence.owner);
        }
      }
    }
  }
  return results.filter(result => result.matchTypes.length);
}
